import React from 'react'
import { v4 as uuidV4 } from 'uuid'
import propTypes from 'prop-types'
import { Table, TableRow, TableHead, TableContainer, TableCell, TableBody, TableSortLabel } from '@material-ui/core'
import { Skeleton } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'

/**
 * Builds a <colgroup> from a headers array, used to give the nested partiality table the same
 * column widths as the parent table above it (both use table-layout: fixed for exact alignment).
 */
const buildColgroup = (headers) => (
	<colgroup>
		{headers.map((header) => (
			<col width={header.width ? `${header.width}` : '*'} key={header.dataSource || uuidV4()} />
		))}
	</colgroup>
)

const useStyle = makeStyles(() => ({
	tableContainer: {
		overflowY: 'auto'
	},
	cell: {
		padding: '6px 10px 6px 10px'
	},
	cellRed: {
		padding: '6px 10px',
		backgroundColor: 'rgba(255, 99, 71, 0.2)'
	}
}))

/**
 * Application table
 */
export const DataGrid = (props) => {
	const classes = useStyle()
	const hasHeaderConfiguration = props.headers.length > 0
	const hasDataItems = props.data.length > 0
	const { sortBy, sortDirection } = props
	/**
	 * Get the visible columns to display in the datagrid
	 */
	const getVisibleColumns = (headerConfig) => headerConfig.filter((header) => header.visible !== false)

	/**
	 * On sort criteria change
	 * @param {*} dataSource
	 */
	const onSortCriteriaChange = (dataSource) => {
		let updatedSortCriteria
		if (dataSource === sortBy) {
			updatedSortCriteria = {
				by: dataSource,
				direction: sortDirection === 'asc' ? 'desc' : 'asc'
			}
		} else {
			updatedSortCriteria = { by: dataSource, direction: 'desc' }
		}
		props.onChangeSortingCriteria(updatedSortCriteria)
	}

	/**
	 * Get the table's header rows
	 */
	const getTableHeadersRows = () => {
		if (hasHeaderConfiguration) {
			return getVisibleColumns(props.headers).map((header) => {
				let isSorted = sortBy === header.dataSource
				return (
					<DataGridColumn
						{...header}
						key={uuidV4()}
						isSorted={isSorted}
						sortDirection={sortDirection}
						onSortCriteriaChange={onSortCriteriaChange}
					/>
				)
			})
		} else {
			if (hasDataItems) {
				let firstitem = props.data[0]
				return Object.entries(firstitem).map((entry) => {
					return <DataGridColumn title={entry[0]} dataSource={entry[0]} key={uuidV4()} />
				})
			}
		}
	}

	/**
	 * Get the columns width
	 */
	const getColumnsWidth = () => {
		if (hasHeaderConfiguration) {
			return buildColgroup(props.headers)
		} else {
			return null
		}
	}

	/**
	 * Get the table rows
	 */
	const getTableRows = (tableColumns) => {
		if (props.isLoading) {
			let emptyArray = Array.from(Array(5).keys()).map(() => null)
			return emptyArray.map((emptyItem) => {
				return <DataGridRow classes={classes} item={emptyItem} tableColumns={tableColumns} key={uuidV4()} />
			})
		} else {
			return props.data
				.filter((item) => item.ParentId === null)
				.map((item, index) => {
					const rowBackgroundColor = '#fff'
					return (
						<DataGridRow
							classes={classes}
							item={item}
							tableColumns={tableColumns}
							childHeaders={props.childHeaders}
							onItemClick={props.onItemClick}
							isExpandedPanel={props.isExpandedPanel}
							expandedPanelComponent={props.expandedPanelComponent}
							expandedParents={props.expandedParents}
							key={uuidV4()}
							data={props.data}
							rowBackgroundColor={rowBackgroundColor}
						/>
					)
				})
		}
	}

	let headerRows = getTableHeadersRows()
	let tableRows = getTableRows(headerRows)

	return (
		<TableContainer>
			<Table size='small' style={{ tableLayout: 'fixed', width: '100%' }}>
				{getColumnsWidth()}
				<TableHead>
					<TableRow>{headerRows}</TableRow>
				</TableHead>
				<TableBody className={classes.tableContainer}>{tableRows}</TableBody>
			</Table>
		</TableContainer>
	)
}
DataGrid.propTypes = {
	data: propTypes.arrayOf(propTypes.object).isRequired,
	headers: propTypes.arrayOf(
		propTypes.shape({
			title: propTypes.string.isRequired,
			dataSource: propTypes.string,
			sortable: propTypes.bool,
			onRenderProperty: propTypes.func,
			width: propTypes.any,
			visible: propTypes.bool
		})
	),
	childHeaders: propTypes.array,
	sortBy: propTypes.string,
	sortDirection: propTypes.oneOf(['asc', 'desc']),
	isLoading: propTypes.bool.isRequired,
	onChangeSortingCriteria: propTypes.func.isRequired,
	onItemClick: propTypes.func,
	isExpandedPanel: propTypes.bool,
	expandedParents: propTypes.object
}
DataGrid.defaultProps = {
	data: [],
	headers: [],
	childHeaders: null,
	sortBy: null,
	isLoading: false,
	sortDirection: 'desc',
	onChangeSortingCriteria: () => console.warn('Callback [onChangeSortingCriteria] no defined')
}

export default DataGrid

/**
 * Table header cell
 */
const DataGridColumn = (props) => {
	let { title, dataSource, sortable, isSorted, sortDirection } = props

	return (
		<TableCell variant='head'>
			{sortable ? (
				<TableSortLabel
					active={isSorted}
					direction={sortDirection == null ? 'desc' : sortDirection}
					onClick={() => props.onSortCriteriaChange(dataSource)}
				>
					{title}
				</TableSortLabel>
			) : (
				title
			)}
		</TableCell>
	)
}
DataGridColumn.propTypes = {
	title: propTypes.string.isRequired,
	dataSource: propTypes.string,
	sortable: propTypes.bool,
	isSorted: propTypes.bool,
	sortDirection: propTypes.string,
	onSortCriteriaChange: propTypes.func.isRequired
}
DataGridColumn.defaultProps = {
	title: '',
	sortable: false,
	isSorted: false,
	sortDirection: 'desc',
	onSortCriteriaChange: () => console.warn('No [onSortLabelClick] callback defined')
}

/**
 * Item table row component
 */
const DataGridRow = (props) => {
	const { item, tableColumns, rowBackgroundColor, classes } = props

	const onItemClick = (sender, item) => {
		if (props.onItemClick) props.onItemClick(sender, item)
	}

	/**
	 * Get the cell content from a column element
	 */
	const getCellContent = (column, item) => {
		let value = column.props.onRenderProperty ? column.props.onRenderProperty(item) : item[column.props.dataSource]
		if (typeof value === 'boolean') value = value.toString()
		return value
	}

	/**
	 * Get the cell content from a header config object (for child rows)
	 */
	const getChildCellContent = (headerConfig, item) => {
		let value = headerConfig.onRenderProperty ? headerConfig.onRenderProperty(item) : item[headerConfig.dataSource]
		if (typeof value === 'boolean') value = value.toString()
		return value
	}

	const isParentExpanded = props.expandedParents && props.expandedParents.has(item?.Id)

	const getChildren = (parent) => {
		if (!isParentExpanded) return null

		let children = []
		if (props.data) children = props.data.filter((item) => item.ParentId === parent.Id)
		if (children.length === 0) return null

		const parentColCount = tableColumns.length
		const hasChildHeaders = props.childHeaders && props.childHeaders.length > 0

		return (
			<TableRow key={`children-${parent.Id}`}>
				<TableCell colSpan={parentColCount} style={{ padding: 0 }}>
					{/* No header row here: child columns are aligned positionally with the parent's own
					    header above, so repeating column titles for each partiality section would be redundant. */}
					<Table size='small' style={{ tableLayout: 'fixed', width: '100%' }}>
						{hasChildHeaders && buildColgroup(props.childHeaders)}
						<TableBody>
							{children.map((child) => (
								<TableRow key={child.Id} style={{ backgroundColor: '#eaf3f8' }}>
									{hasChildHeaders
										? props.childHeaders.map((header, idx) => {
												let childValue = getChildCellContent(header, child)
												return (
													<TableCell
														className={child.SparePartSaleStatus === 2 ? classes.cellRed : classes.cell}
														key={`${child.Id}-${header.dataSource}`}
														style={idx === 0 ? { borderLeft: '3px solid #5357DC' } : undefined}
													>
														{childValue}
													</TableCell>
												)
										  })
										: tableColumns.map((column, idx) => {
												let childValue = getCellContent(column, child)
												return (
													<TableCell
														className={child.SparePartSaleStatus === 2 ? classes.cellRed : classes.cell}
														key={`${child.Id}-${column.props.dataSource}`}
														style={idx === 0 ? { borderLeft: '3px solid #5357DC' } : undefined}
													>
														{childValue}
													</TableCell>
												)
										  })}
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableCell>
			</TableRow>
		)
	}

	if (item === null) {
		return (
			<TableRow>
				{tableColumns.map(() => {
					return (
						<TableCell key={uuidV4()}>
							<Skeleton animation='wave' />
						</TableCell>
					)
				})}
			</TableRow>
		)
	} else if (props.isExpandedPanel) {
		return (
			<>
				<TableRow hover onClick={(sender) => onItemClick(sender, item)} style={{ backgroundColor: rowBackgroundColor }}>
					{tableColumns.map((column, idx) => {
						let value = getCellContent(column, item)
						return (
							<TableCell
								className={item.SparePartSaleStatus === 2 ? classes.cellRed : classes.cell}
								key={uuidV4()}
								style={idx === 0 && isParentExpanded ? { borderLeft: '3px solid #5357DC' } : undefined}
							>
								{value}
							</TableCell>
						)
					})}
				</TableRow>
				{props.expandedPanelComponent(item)}
				{getChildren(item)}
			</>
		)
	} else {
		return (
			<React.Fragment key={item.Id}>
				<TableRow hover onClick={(sender) => onItemClick(sender, item)} style={{ backgroundColor: rowBackgroundColor }}>
					{tableColumns.map((column, idx) => {
						let value = getCellContent(column, item)
						return (
							<TableCell
								className={classes.cell}
								key={uuidV4()}
								style={idx === 0 && isParentExpanded ? { borderLeft: '3px solid #5357DC' } : undefined}
							>
								{value}
							</TableCell>
						)
					})}
				</TableRow>
				{getChildren(item)}
			</React.Fragment>
		)
	}
}
DataGridRow.propTypes = {
	item: propTypes.object,
	tableColumns: propTypes.arrayOf(propTypes.element),
	childHeaders: propTypes.array,
	onItemClick: propTypes.func,
	isExpandedPanel: propTypes.bool,
	expandedParents: propTypes.object
}
